import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import { KineticLines, Reveal, SectionLabel } from "../components/Reveal";
import ProfitabilityEngine from "../components/home/ProfitabilityEngine";
import ExpenseFlow from "../components/home/ExpenseFlow";
import ServiceBento from "../components/home/ServiceBento";
import ProcessTimeline from "../components/home/ProcessTimeline";
import HistorySection from "../components/home/HistorySection";
import TrustSection from "../components/home/TrustSection";
import FinalCTA from "../components/home/FinalCTA";
import FAQ, { HOME_FAQS } from "../components/FAQ";
import AssessmentWizard from "../components/AssessmentWizard";
import Marquee from "../components/Marquee";
import { LogoMark } from "../components/Logo";
import { ArrowRight } from "lucide-react";

function Hero() {
  return (
    <section
      className="relative overflow-hidden bg-navy-950 text-white pt-32 lg:pt-40 pb-16 md:pb-24 grain"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-arch-dark" aria-hidden="true" />
      <div className="absolute -top-40 right-[4%] w-[640px] h-[640px] rounded-full bg-teal/15 blur-[130px]" aria-hidden="true" />
      <div className="absolute top-1/3 left-[-10%] w-[520px] h-[520px] rounded-full bg-royal/25 blur-[120px]" aria-hidden="true" />
      <svg className="absolute inset-x-0 bottom-0 h-64 w-full opacity-30" viewBox="0 0 1440 260" fill="none" preserveAspectRatio="none" aria-hidden="true">
        <path d="M-100 210 C 300 140, 560 260, 900 170 C 1180 100, 1320 200, 1560 120" stroke="#15B8A6" strokeWidth="1.4" strokeDasharray="5 12" className="animate-dashFlow" />
        <path d="M-100 150 C 320 90, 620 210, 940 120 C 1200 50, 1360 140, 1560 60" stroke="#3D6BA8" strokeWidth="1.2" strokeDasharray="4 14" className="animate-dashFlow" style={{ animationDelay: "0.8s" }} />
        <path d="M-100 90 C 340 40, 660 160, 980 80 C 1220 20, 1380 90, 1560 30" stroke="#8FB3D9" strokeWidth="1" strokeDasharray="3 15" className="animate-dashFlow" style={{ animationDelay: "1.4s" }} />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.02fr_0.98fr] gap-12 lg:gap-6 items-center">
          <div>
            <Reveal delay={0}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-teal/40 bg-teal/10 px-4 py-2 text-[11px] font-bold uppercase tracking-wider2 text-teal-bright">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-bright" aria-hidden="true" />
                  A division of Community Promotions
                </span>
                <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-[11px] font-bold uppercase tracking-wider2 text-ice/70">
                  Helping businesses since 2001
                </span>
              </div>
            </Reveal>

            <KineticLines
              animateOnLoad
              delay={0.15}
              as="h1"
              className="mt-7 font-display font-extrabold tracking-tightest text-white text-[2.7rem] sm:text-6xl lg:text-[4.1rem] leading-[1.03]"
              lines={[
                "You're Working Hard",
                "to Make More Money.",
                <>
                  Let's Help You{" "}
                  <span className="relative inline-block text-teal-bright whitespace-nowrap">
                    Keep More of It.
                    <svg className="absolute -bottom-1.5 left-0 w-full" viewBox="0 0 240 10" fill="none" aria-hidden="true">
                      <motion.path
                        d="M3 7 C 60 2, 150 2, 237 6"
                        stroke="#2BD4C1"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </svg>
                  </span>
                </>,
              ]}
            />

            <Reveal delay={0.35}>
              <div className="mt-7 max-w-xl space-y-4 text-base md:text-lg leading-relaxed text-ice/75">
                <p>
                  Running a business is expensive. From technology and employee benefits to operating costs and
                  potential tax incentives, opportunities to improve profitability can be easy to overlook.
                </p>
                <p>
                  We help business owners discover practical opportunities, connect with trusted resources, and make
                  smarter decisions about their bottom line.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.45}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href="#savings-review"
                  data-testid="hero-explore-savings-button"
                  className="group inline-flex items-center justify-center gap-2 bg-teal text-navy font-bold text-[15px] px-7 py-4 rounded-full transition-all duration-300 hover:bg-teal-bright hover:-translate-y-0.5 shadow-[0_16px_40px_-12px_rgba(21,184,166,0.55)]"
                >
                  Explore My Savings Opportunities
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href="#how-it-works"
                  data-testid="hero-how-it-works-button"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-bold text-[15px] px-7 py-4 rounded-full transition-all duration-300 hover:border-teal-bright hover:text-teal-bright"
                >
                  See How It Works
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.55}>
              <div className="mt-9 flex items-center gap-3" data-testid="hero-trust-line">
                <span className="rounded-lg ring-1 ring-white/20 p-0.5">
                  <LogoMark size={30} tile />
                </span>
                <p className="text-[13px] leading-snug text-ice/70">
                  From the people behind <span className="font-bold text-white">Community Promotions</span> — helping
                  businesses grow since 2001.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="relative lg:pl-2">
            <ProfitabilityEngine dark />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="page-home">
      <Seo
        title="Business Savings & Profitability Review"
        description="Discover hidden opportunities in your business — expenses, technology, employee benefits and tax incentives. Helping good businesses keep more of what they earn."
        path="/"
      />
      <Hero />
      <Marquee />
      <ExpenseFlow />
      <ServiceBento />

      <section id="savings-review" className="relative py-16 md:py-24 bg-white scroll-mt-20" data-testid="savings-review-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <SectionLabel>The Savings Review</SectionLabel>
            <KineticLines
              as="h2"
              className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.08]"
              lines={["Where Could Your Business", <>Be <span className="text-teal-dark">Missing Opportunities?</span></>]}
            />
            <Reveal delay={0.1}>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-slate">
                Answer four quick questions and we'll help you see which specialist resources may be worth exploring.
                No jargon, no pressure — and no savings estimate, just an honest starting point.
              </p>
            </Reveal>
          </div>
          <div className="mt-10 max-w-3xl mx-auto">
            <AssessmentWizard />
          </div>
        </div>
      </section>

      <ProcessTimeline />
      <HistorySection />
      <TrustSection />
      <FAQ items={HOME_FAQS} />
      <FinalCTA />
    </main>
  );
}
